import * as React from "react"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shadcn/components/ui/table"
import { accountFallback, postsLookback, social as labels } from "../copy"
import type { Destination, SocialBucket } from "../types"

/** Outcome keys are stored slugs, so they need bracket access, not dot access. */
const OUTCOME_COLUMNS: ReadonlyArray<{ key: keyof Destination; label: string }> = [
  { key: "ok", label: labels.colOk },
  { key: "dlvr-throttled", label: labels.colThrottled },
  { key: "platform-rejected", label: labels.colRejected },
  { key: "subscription-limited", label: labels.colLimit },
  { key: "other-error", label: labels.colOther },
]

export default function SocialDetails({ bucket }: { bucket: SocialBucket }) {
  const rows = bucket.perDestination ?? []
  if (rows.length === 0) {
    return <p className="tw:text-sm tw:text-muted-foreground">{bucket.note ?? labels.noData}</p>
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      {/* One muted line rather than five stacked definitions. The legend
          restated every column name in prose directly above the header row that
          already names them, which made it the densest block in the report. */}
      <div className="tw:flex tw:flex-col tw:gap-0.5">
        <p className="tw:text-sm tw:text-muted-foreground">
          {postsLookback(bucket.lookbackDays ?? 3)}
        </p>
        <p className="tw:text-xs tw:leading-5 tw:text-muted-foreground">
          {labels.legend.map((entry, idx) => (
            <React.Fragment key={entry.term}>
              {idx > 0 && <span aria-hidden="true"> · </span>}
              <span className="tw:font-semibold tw:text-foreground">{entry.term}</span>{" "}
              {entry.definition.replace(/\.$/, "")}
            </React.Fragment>
          ))}
        </p>
      </div>

      {/* Six columns don't fit a narrow embedded panel. Table supplies its own
          overflow-x-auto, so this container only needs to clip and frame it —
          matching account/PaymentHistory.tsx. min-w keeps the columns legible
          and lets them scroll rather than crush. */}
      <div className="tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-card">
        <Table className="tw:min-w-[540px]">
          <TableHeader>
            <TableRow>
              <TableHead>{labels.colDestination}</TableHead>
              {OUTCOME_COLUMNS.map((column) => (
                <TableHead key={String(column.key)} className="tw:text-right">
                  {column.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((destination) => (
              <TableRow key={destination.paId}>
                <TableCell>
                  <span className="tw:font-semibold">
                    {destination.name || accountFallback(destination.paId)}
                  </span>
                  {destination.service && (
                    <span className="tw:text-muted-foreground"> · {destination.service}</span>
                  )}
                </TableCell>
                {OUTCOME_COLUMNS.map((column) => (
                  <TableCell
                    key={String(column.key)}
                    className="tw:text-right tw:tabular-nums"
                  >
                    {destination[column.key] ?? 0}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
